import CachedIcon from "@mui/icons-material/Cached";
import ClearIcon from "@mui/icons-material/Clear";
import CloudDownload from "@mui/icons-material/CloudDownload";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import CloseDialog from "components/CloseDialog";
import LazyTable, {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  AttendanceRecordFilter,
  AttendanceRecordFilterRequest,
  BypassSkuAttendanceRequest,
  ListRequestOfAttendanceRecordFilter,
  ListRequestOfAttendanceRecordFilterRequest,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { SnackbarMessageProps } from "types/common-types";
import AttendanceRecordStatusSelector from "./AttendanceRecordStatusSelector";

const AttendanceRecords = () => {
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("initialEntryTime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [statusIdFilter, setStatusIdFilter] = useState<string>();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const searchTerm = persistanceStore.useState(
    (s) => s.skuAttendanceSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [dialogOpener, dialogOpenerActions] = useBoolean(false);

  const {
    eventsUrl,
    skuService,
    attendanceRecordService,
    webinarService,
    attendanceRecordsDownloadUrl,
  } = useGlobalContext();

  const [
    confirmProcessAttendanceOpen,
    confirmProcessAttendanceOpenActions,
  ] = useBoolean(false);

  const [changeReason, setChangeReason] = useState("");

  const { id: skuId, orgId: organizationId } = useParams<{
    id: string;
    orgId: string;
  }>();

  const processAttendanceForAllUsers = async () => {
    await webinarService.bypassAttendanceForSku(
      new BypassSkuAttendanceRequest({
        skuId,
        changeReason,
      })
    );
    queryClient.invalidateQueries(["attendanceRecords", skuId]);

    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "attendanceProcessingForAllUsersHasStarted" })
      )
    );

    confirmProcessAttendanceOpenActions.setFalse();
  };

  const exportAttendanceRecords = async () => {
    dialogOpenerActions.setTrue();
    await attendanceRecordService.exportAttendanceRecords(
      skuId!,
      ListRequestOfAttendanceRecordFilterRequest.fromJS({
        parameters: AttendanceRecordFilterRequest.fromJS({
          attendanceRecordsDownloadUrl: attendanceRecordsDownloadUrl(skuId, ""),
          attendanceRecordFilter: AttendanceRecordFilter.fromJS({
            skuId,
            attendanceRecordStatusId: statusIdFilter,
            searchText:
              debouncedSearchTerm === "" ? undefined : debouncedSearchTerm,
          }),
        }),
      })
    );
  };

  const refresh = () => {
    queryClient.invalidateQueries(["attendanceRecords", skuId]);
  };

  const skuResponse = useQuery(
    ["webinar", skuId],
    () => skuService.getSkuById(skuId!),
    {
      enabled: !!skuId,
    }
  );

  const setTerm = (term: string) => {
    persistanceStore.update((s) => {
      s.skuAttendanceSearchTerm = term;
    });
  };

  const sku = skuResponse.data?.data;
  useTitle(
    `${t({ id: "resultsOfAttendanceProcess" })}: ${sku?.sku || "Webinar"}`
  );

  const { data, isFetching } = useQuery(
    [
      "attendanceRecords",
      skuId,
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      statusIdFilter,
      debouncedSearchTerm,
    ],
    () =>
      attendanceRecordService.getAttendanceRecords(
        ListRequestOfAttendanceRecordFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: AttendanceRecordFilter.fromJS({
            skuId,
            attendanceRecordStatusId: statusIdFilter,
            searchText:
              debouncedSearchTerm === "" ? undefined : debouncedSearchTerm,
          }),
        })
      ),
    { enabled: !!skuId && !!sku }
  );

  const basicCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...basicCell,
      accessor: "firstName",
      label: t({ id: "firstNameLabel" }),
    },
    {
      ...basicCell,
      accessor: "lastName",
      label: t({ id: "lastNameLabel" }),
    },
    {
      ...basicCell,
      accessor: "email",
      label: t({ id: "emailLabel" }),
    },
    {
      ...basicCell,
      accessor: "status",
      customSortAccessor: "attendanceRecordStatus.name",
      label: t({ id: "statusLabel" }),
    },
    {
      ...basicCell,
      accessor: "initialEntryTime",
      type: "datetime",
      label: t({ id: "entryTime" }),
    },
    {
      ...basicCell,
      accessor: "finalExitTime",
      type: "datetime",
      label: t({ id: "exitTime" }),
    },
    {
      ...basicCell,
      accessor: "timeInSession",
      label: t({ id: "timeInSession" }),
    },
    {
      ...basicCell,
      accessor: "totalPresenceChecks",
      label: t({ id: "popUpsAsked" }),
    },
    {
      ...basicCell,
      accessor: "presenceChecksAnswered",
      label: t({ id: "popUpsAnswered" }),
    },
    {
      ...basicCell,
      accessor: "presence",
      label: t({ id: "presence" }),
      type: "node",
      noSort: true,
      View: (value: any) => <span>{value.value.presence}%</span>,
    },
  ];

  const titleComponent = (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Typography variant="h6">
        <T id="webinar" />
        {": "}
        {skuResponse.isLoading ? <CircularProgress size={16} /> : sku?.title}
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Tooltip
          title={t({
            id: "refresh",
          })}
        >
          <IconButton onClick={refresh}>
            <CachedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box mb={3}>
        <RedirectBackButton
          redirectRoute={eventsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <h1>{t({ id: "resultsOfAttendanceProcess" })}</h1>
      <Box mb={3} width="100%">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "attendanceSearchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setTerm("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) => setTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <AttendanceRecordStatusSelector onChange={setStatusIdFilter} />
          </Grid>
          <Grid item xs={12} sm={2} md={2.5}>
            <Button
              type="submit"
              color="primary"
              variant="outlined"
              startIcon={<CloudDownload />}
              onClick={exportAttendanceRecords}
              style={{ height: "100%" }}
              fullWidth
            >
              <T id="exportResults" />
            </Button>
          </Grid>
          <Grid item xs={12} sm={2} md={2.5}>
            <Button
              variant="outlined"
              onClick={confirmProcessAttendanceOpenActions.setTrue}
              style={{ height: "100%" }}
              fullWidth
            >
              <T id="markAllUsersAsAttended" />
            </Button>
          </Grid>
        </Grid>
      </Box>
      <ChangeReasonDialog
        open={confirmProcessAttendanceOpen}
        onCancel={confirmProcessAttendanceOpenActions.setFalse}
        handleConfirm={processAttendanceForAllUsers}
        changeReason={changeReason}
        setChangeReason={setChangeReason}
        titleKey="markAllUsersAsAttended"
      />
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: data?.data ?? [],
          loading: isFetching,
          titleComponent,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />
      <CloseDialog
        dialogTitleKey="exportProcessing"
        contentKey="exportProcessingMessage"
        open={dialogOpener}
        onClose={dialogOpenerActions.setFalse}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default AttendanceRecords;
